import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import ReactPlayer from 'react-player/lazy';

import LazyImage from '../lazy-image';
import { motion } from 'framer-motion';

interface SystemAsset {
  alt: string;
  copyright: string;
  fieldtype: string;
  filename: string;
  focus: string;
  id: number;
  is_external_url: boolean;
  meta_data: Record<string, any>;
  name: string;
  source: string;
  title: string;
}

interface SystemEntryMediaCardProps {
  system: {
    systemAsset: SystemAsset;
  };
}

const MediaContainer = styled(motion.div)`
  position: relative;
  width: 100%;
  height: 0;
  padding-bottom: 75%;
  background: #f8f8f8;
  overflow: hidden;
  border-radius: var(--Radius-Radius-3, 16px);
  @media (min-width: 992px) {
    padding-bottom: 56.25%;
  }
`;

const VideoPlayer = styled(ReactPlayer)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  video {
    object-fit: cover;
  }
`;

function isVideo(filename: string): boolean {
  if (!filename) return false;

  const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi', '.mkv'];
  return videoExtensions.some((ext) => filename.toLowerCase().includes(ext));
}

function isImage(filename: string): boolean {
  if (!filename) return false;

  const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg'];
  return imageExtensions.some((ext) => filename.toLowerCase().includes(ext));
}

function getAssetType(asset: SystemAsset): 'video' | 'image' | null {
  if (!asset || !asset.filename) return null;

  if (isVideo(asset.filename)) return 'video';
  if (isImage(asset.filename)) return 'image';

  return null;
}

export default function SystemEntryMediaCard({
  system
}: SystemEntryMediaCardProps) {
  const { systemAsset } = system;
  const [playerPlaying, setPlayerPlaying] = useState(true);
  const videoRef = useRef<ReactPlayer>(null);

  if (!systemAsset) {
    return null;
  }

  const assetType = getAssetType(systemAsset);

  if (!assetType) {
    return null;
  }

  function handlePlaying() {
    setPlayerPlaying(true);
  }

  function handleEnded() {
    if (videoRef.current && videoRef.current.seekTo) {
      videoRef.current.seekTo(0);
    }
    setPlayerPlaying(false);
    setTimeout(() => {
      setPlayerPlaying(true);
    }, 1);
  }

  return (
    <MediaContainer
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3, ease: [0.26, 1, 0.5, 1] }}
      exit={{ opacity: 0 }}
      key={systemAsset.id}
    >
      {assetType === 'video' && (
        <VideoPlayer
          url={systemAsset.filename}
          ref={videoRef}
          width="100%"
          height="100%"
          playing={playerPlaying}
          controls={false}
          playsinline={true}
          muted={true}
          loop={true}
          onPlay={handlePlaying}
          onEnded={handleEnded}
        />
      )}

      {assetType === 'image' && (
        <LazyImage
          url={systemAsset.filename}
          alt={systemAsset.alt || systemAsset.name || ''}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover'
          }}
        />
      )}
    </MediaContainer>
  );
}
